import axios from 'axios'
import Vue from 'vue'

/**
 * JWT工具类，用于处理JWT令牌的获取和API请求
 */
class JwtService {
    constructor() {
        // 根据环境动态设置网关地址
        if (process.env.NODE_ENV === 'development') {
            // 开发环境使用完整域名
            this.gatewayUrl = 'https://www.pixysoft.cn/gateway-test'
            this.authUrl = 'https://www.pixysoft.cn/gateway-test/website/token'
        } else {
            // 生产环境使用相对路径，自动匹配当前域名
            this.gatewayUrl = '/gateway'
            this.authUrl = '/gateway/website/token'
        }

        // 创建axios实例
        this.instance = axios.create({
            timeout: 10000 // 请求超时时间
        })

        // 响应拦截器
        this.instance.interceptors.response.use(
            (response) => {
                return response
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }

    /**
     * 获取JWT令牌
     * @param {string} scope - 访问范围，指定要访问的API路径
     * @returns {Promise<string>} - JWT令牌
     */
    async getToken(scope) {
        try {
            // 优化scope，去除?及其后面的参数
            let pureScope = scope
            if (typeof scope === 'string') {
                const idx = scope.indexOf('?')
                if (idx !== -1) {
                    pureScope = scope.substring(0, idx)
                }
            }
            const response = await this.instance.post(this.authUrl, {
                scope: pureScope
            })

            // 新的返回结构：token 在 data.access_token
            if (response?.data?.access_token) {
                return response.data.access_token
            } else {
                throw new Error('Failed to get JWT token')
            }
        } catch (error) {
            console.error('Error getting JWT token:', error)
            throw error
        }
    }

    /**
     * 使用JWT令牌发送请求
     * @param {string} method - 请求方法 (get, post, put, delete等)
     * @param {string} path - 目标API路径
     * @param {Object} data - 请求数据
     * @param {Object} options - 额外的请求选项
     * @returns {Promise<any>} - API响应
     */
    async request(method, path, data = {}, options = {}) {
        // 获取JWT令牌
        const token = await this.getToken(path)

        // 构建请求配置
        const config = {
            method,
            url: `${this.gatewayUrl}/website${path}`,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': options.contentType || 'application/json'
            },
            ...options
        }

        // 根据请求方法设置数据
        if (method.toLowerCase() === 'get') {
            config.params = data
        } else {
            config.data = data
        }

        return await this.instance(config)
    }

    /**
     * 发送GET请求
     * @param {string} path - 目标API路径
     * @param {Object} params - 请求参数
     * @param {Object} options - 额外的请求选项
     * @returns {Promise<any>} - API响应
     */
    async get(path, params = {}, options = {}) {
        return this.request('get', path, params, options)
    }

    /**
     * 发送POST请求
     * @param {string} path - 目标API路径
     * @param {Object|FormData} data - 请求数据
     * @param {Object} options - 额外的请求选项
     * @returns {Promise<any>} - API响应
     */
    async post(path, data = {}, options = {}) {
        return this.request('post', path, data, options)
    }

    /**
     * 发送FormData格式的POST请求
     * @param {string} path - 目标API路径
     * @param {Object} data - 要转换为FormData的对象
     * @param {Object} options - 额外的请求选项，如onUploadProgress回调
     * @returns {Promise<any>} - API响应
     */
    async postFormData(path, formData, options = {}) {
        // 合并options，确保contentType正确
        const mergedOptions = {
            ...options,
            contentType: 'multipart/form-data'
        }

        return this.post(path, formData, mergedOptions)
    }
}

// 创建单例实例
const jwtService = new JwtService()

// 添加到Vue原型，使其在所有组件中可用
Vue.prototype.$jwt = jwtService

export default jwtService
